import {getServerValue} from './Helpers.js';

const regex = new RegExp(/(image|video)\-([a-z0-9])\/([a-z0-9\-].*?)\/index\.(\w*)/g);
const CDN = getServerValue("__CDN__");

/**
 * 
 * Try to get a local url from an string or URL or Local Object
 * @param url string or URL or local object
 * @returns URL | undefined
 */

export function isLocalURL(urlOrObject) {

	// Internal object?
	if (typeof urlOrObject === 'object' && (urlOrObject.id || urlOrObject.external_hash)) {

		let inputURL = (urlOrObject.url || "").toString();

		//	no usable CDN configured (PUBLIC_CDN unset) — degrade to "not a
		//	local media URL" instead of throwing on new URL()
		let outputURL;
		try {
			outputURL = new URL(CDN);
		} catch (_e) {
			console.warn('isLocalURL: PUBLIC_CDN is not configured or invalid:', CDN);
			return undefined;
		}
		let cdnPathName = outputURL.pathname;

		if (inputURL.indexOf('http://') === 0 || inputURL.indexOf('https://') === 0) {
			try {
				const x = new URL(inputURL);
				const y = new URL(CDN);

				let xsp = x.pathname.split('/');
				const d = xsp.length - y.pathname.split('/').length;
				xsp = xsp.slice(d - 1);
				outputURL.pathname = xsp.join('/');
				outputURL.search = x.search;
			} catch (e) {
				console.error(`invalid url`, urlOrObject, CDN);
				throw e;
			}
		} else {
			outputURL.pathname = inputURL;
		}

		// signed external image (proxy via cdn/content server)
		if (urlOrObject.external_hash) {
			outputURL.pathname = 'external_image';
			outputURL.search = `s=${encodeURIComponent(urlOrObject.url)}&sig=${encodeURIComponent(urlOrObject.external_hash)}`;
		}

		outputURL.pathname = `${cdnPathName}${outputURL.pathname}`.replace('//', '/');

		return {...urlOrObject, url: outputURL};
	}

	let r = [];
	let match;

	let url = urlOrObject.toString();

	while (match = regex.exec(url)) {
		r.push(...match.slice(1))
	}

	if (r.length === 0) {
		return;
	}

	if (r[1] != r[2].substring(0, 1)) {
		return;
	}

	// not our cdn, but similar structure. nice
	if ((url.indexOf('http://') === 0 || url.indexOf('https://') === 0) && url.indexOf(CDN) !== 0) {
		return;
	}

	if (!CDN || CDN === '__CDN__') {
		console.warn('isLocalURL: PUBLIC_CDN is not configured or invalid:', CDN);
		return undefined;
	}

	let qs = url.toString().split('?');

	if (qs.length > 1) {
		qs = '?' + qs.pop();
	} else {
		qs = '';
	}

	return {
		id: r[2],
		type: r[0],
		extension: r[3],
		url: new URL(`${CDN}/${r[0]}-${r[1]}/${r[2]}/index.${r[3]}${qs}`.replace('//', '/'))
	}
}

/**
 * Get the url of a media object from a URL, if local append qs otherwise returns the original
 * @param url string or URL
 * @param withQueryStringParams key value object to append to query string
 * @returns URL | undefined
 */
export function getURL(url, withQueryStringParams) {

	const local = isLocalURL(url);

	if (!local) {
		//	media objects must still yield a usable string (e.g. when the CDN
		//	env is not configured) — never leak the object into an img src
		if (url && typeof url === 'object') {
			return (url.url || '').toString();
		}
		return url;
	}

	const localURL = local.url;

	if (withQueryStringParams && Object.keys(withQueryStringParams).length > 0) {

		const existingParams = new URLSearchParams(localURL.search);
		for (let [key, value] of Object.entries(withQueryStringParams)) {
			existingParams.set(key, value);
		}

		localURL.search = existingParams;
	}

	return localURL.toString();
}


/**
 * Effective photo/video credit for a block. Block-level values (image/video
 * blocks: data.source / data.source_url; gallery entries: credit / credit_url)
 * override the media-level ones copied into the snapshot (media.source,
 * media.source_url — refreshed server-side on every version save).
 * @returns {{text: string, url: string} | null}
 */
export function mediaCredit(override, media) {
	//	gallery entries are their own snapshot (override === media), so the
	//	per-gallery `credit` must be checked before the media-level `source`
	const overrideText = override ? (override.credit || (override !== media ? override.source : "") || "") : "";
	const text = (overrideText || (media && media.source) || "").toString().trim();
	if (!text) return null;
	const url = (overrideText
		? (override.credit ? override.credit_url : override.source_url) || ""
		: ((media && media.source_url) || "")).toString().trim();
	return { text, url: /^https?:\/\//i.test(url) ? url : "" };
}
